// Asks once when the Terms changed, or when an older account never accepted any (boards
// TermsUpdated and TermsUpdatedPhone; docs/plans/terms.md). Online only, never during a sale.
import { Button, Modal } from '@ims/ui'
import { Check } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { useOnline } from '../hooks.ts'
import { PRIVACY_URL, TERMS_CHANGES, TERMS_URL, termsAsk } from '../lib/terms.ts'
import { useSession, useUser } from '../session.tsx'
import { useSignOut } from './sign-out.tsx'

const keyOf = (userId: string) => `ims.terms.${userId}`

function knownAccepted(userId: string): string | null {
  try {
    return localStorage.getItem(keyOf(userId))
  } catch {
    return null
  }
}

function remember(userId: string, version: string) {
  try {
    localStorage.setItem(keyOf(userId), version)
  } catch {
    // Private mode: the server is asked again next time, which is fine.
  }
}

/** A sale or a table's order is never interrupted: the question waits for the next page. */
const busyPath = (path: string) => path === '/bill' || /^\/tables\/[^/]+$/.test(path)

// Asked at most once per session and person, even if the shell mounts again.
const askedThisSession = new Set<string>()

export function TermsGate() {
  const user = useUser()
  const session = useSession()
  const online = useOnline()
  const { pathname } = useLocation()
  const signOut = useSignOut()
  const [ask, setAsk] = useState<{ kind: 'first' | 'updated'; version: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const quiet = busyPath(pathname)

  useEffect(() => {
    if (!online || quiet || askedThisSession.has(user.id)) return
    askedThisSession.add(user.id)
    // Not cancelled when the effect runs again: the session object changes after load, and the
    // answer must still land (the guard above keeps it to one question).
    session.myTerms().then(
      (t) => {
        const kind = termsAsk(t.accepted_version, t.current_version)
        if (kind) setAsk({ kind, version: t.current_version })
        else remember(user.id, t.current_version)
      },
      // Offline or the server did not answer: ask again in a later session.
      () => askedThisSession.delete(user.id),
    )
  }, [online, quiet, user.id, session])

  if (!ask) return null
  // Already accepted on this device (the server answered the same before).
  if (knownAccepted(user.id) === ask.version) return null

  const accept = async () => {
    setBusy(true)
    setFailed(false)
    try {
      await session.acceptTerms(ask.version)
      remember(user.id, ask.version)
      setAsk(null)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const changes = ask.kind === 'updated' ? TERMS_CHANGES : []
  return (
    <>
      <Modal
        open
        required
        size="sm"
        onClose={() => {}}
        title={ask.kind === 'updated' ? 'We’ve updated our Terms' : 'Our Terms of Service'}
        footer={
          <>
            <Button variant="secondary" onClick={() => void signOut.start()} disabled={busy}>
              Sign out
            </Button>
            {/* The answer most people give starts with the focus. */}
            <Button autoFocus onClick={() => void accept()} loading={busy}>
              Accept and continue
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-muted">
            {ask.kind === 'updated'
              ? 'Please read what changed and accept to carry on.'
              : 'Sano Godam now asks everyone to accept its Terms once.'}
          </p>
          <ul className="space-y-2">
            {[
              ...changes,
              'Nothing changes about your data: it stays yours, and you can export it any time.',
            ].map((line) => (
              <li key={line} className="flex gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="text-muted">
            Read them in full:{' '}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium whitespace-nowrap text-primary underline underline-offset-2"
            >
              Terms of Service
            </a>{' '}
            ·{' '}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium whitespace-nowrap text-primary underline underline-offset-2"
            >
              Privacy Policy
            </a>
          </p>
          {failed && (
            <p role="alert" className="text-danger">
              That didn’t go through. Check the connection and try again.
            </p>
          )}
          <p className="text-xs text-subtle">
            Signing out keeps everything on the server. Questions? Call or email us from Help.
          </p>
        </div>
      </Modal>
      {signOut.dialog}
    </>
  )
}
